import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { GraduationCap, Award, Star, Shield, Waves, ShoppingCart, Loader2, ChevronDown, ChevronUp, User, BookOpen, Target } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import ContactBanner from '../components/ContactBanner';
import axios from 'axios';
import { formatItemPrice } from '../lib/currency';
import { usePageHeader } from '../hooks/usePageHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CoursesPage = () => {
  const { addToCart } = useCart();
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCourse, setExpandedCourse] = useState(null);
  const header = usePageHeader('courses', { heading: 'Dive Courses', subheading: 'From your first breath underwater to professional certification. Quality training aligned with international ISO standards.' });

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const res = await axios.get(`${API_URL}/courses`);
      setCourses(res.data);
    } catch (error) {
      console.error('Failed to fetch courses:', error);
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  // Calculate discount based on membership
  const getDiscountedPrice = (price) => {
    if (user?.membership_tier === 'pro') {
      return price * 0.75; // 25% off
    } else if (user?.membership_tier === 'club') {
      return price * 0.80; // 20% off
    }
    return price;
  };

  const discountPercent = user?.membership_tier === 'pro' ? 25 : user?.membership_tier === 'club' ? 20 : 0;

  // Filter courses by category
  const beginnerCourses = courses.filter(c => c.category === 'beginner');
  const advancedCourses = courses.filter(c => c.category === 'advanced');
  const professionalCourses = courses.filter(c => c.category === 'professional');
  const specialtyCourses = courses.filter(c => c.category === 'specialty');

  // Get icon based on course name/level
  const getIcon = (course) => {
    const name = course.name.toLowerCase();
    if (name.includes('discover') || name.includes('intro')) return Waves;
    if (name.includes('open water') || name.includes('entry')) return GraduationCap;
    if (name.includes('advanced')) return Award;
    if (name.includes('rescue')) return Shield;
    if (name.includes('divemaster') || name.includes('professional')) return Star;
    return GraduationCap;
  };

  const handleAddToCart = (course) => {
    const cartItem = {
      id: course.id,
      name: course.name,
      price: course.price,
      description: course.description,
      image_url: course.image_url || "https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=300&h=300&fit=crop",
      category: "courses"
    };
    addToCart(cartItem);
    toast.success(`${course.name} added to cart`);
  };

  const CourseCard = ({ course, featured = false }) => {
    const discountedPrice = getDiscountedPrice(course.price);
    const hasDiscount = discountPercent > 0;
    const Icon = getIcon(course);
    const isExpanded = expandedCourse === course.id;

    const toggleExpand = () => {
      setExpandedCourse(isExpanded ? null : course.id);
    };

    return (
      <div className={`bg-white rounded-lg border ${featured ? 'border-[#FF6B35] shadow-lg' : 'border-gray-100'} overflow-hidden hover:shadow-xl transition-all duration-300`}>
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <Icon className="w-8 h-8 text-[#FF6B35]" strokeWidth={1.5} />
              <div>
                <h3 className="text-xl font-heading font-semibold text-[#0A2342]">{course.name}</h3>
                {course.level && (
                  <Badge variant="outline" className="mt-1 text-xs">
                    {course.level}
                  </Badge>
                )}
              </div>
            </div>
          </div>
          
          <p className="text-gray-600 mb-4 text-sm leading-relaxed">
            {course.description}
          </p>

          {/* Expand/Collapse Button */}
          <button 
            onClick={toggleExpand}
            className="flex items-center gap-2 text-[#FF6B35] font-medium text-sm mb-4 hover:underline"
            data-testid={`expand-course-${course.id}`}
          >
            {isExpanded ? (
              <>
                <ChevronUp className="w-4 h-4" />
                Hide Details
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                View More Details
              </>
            )}
          </button>

          {/* Expanded Details */}
          {isExpanded && (
            <div className="bg-[#F0EFEA] rounded-lg p-4 mb-4 space-y-4 animate-in slide-in-from-top-2 duration-300">
              <h4 className="font-semibold text-[#0A2342] border-b border-[#0A2342]/10 pb-2">Course Details</h4>
              
              {course.min_age && (
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-[#FF6B35] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-[#0A2342] text-sm">Minimum Age</p>
                    <p className="text-gray-600 text-sm">{course.min_age} years old</p>
                  </div>
                </div>
              )}
              
              {course.what_to_expect && (
                <div className="flex items-start gap-3">
                  <BookOpen className="w-5 h-5 text-[#FF6B35] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-[#0A2342] text-sm">What to Expect</p>
                    <p className="text-gray-600 text-sm whitespace-pre-line">{course.what_to_expect}</p>
                  </div>
                </div>
              )}
              
              {course.career_opportunities && (
                <div className="flex items-start gap-3">
                  <Target className="w-5 h-5 text-[#FF6B35] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-[#0A2342] text-sm">Career Opportunities</p>
                    <p className="text-gray-600 text-sm whitespace-pre-line">{course.career_opportunities}</p>
                  </div>
                </div>
              )}
              
              {course.fun_fact && (
                <div className="flex items-start gap-3">
                  <Star className="w-5 h-5 text-[#FF6B35] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-[#0A2342] text-sm">Fun Fact</p>
                    <p className="text-gray-600 text-sm whitespace-pre-line">{course.fun_fact}</p>
                  </div>
                </div>
              )}
              
              {!course.min_age && !course.what_to_expect && !course.career_opportunities && !course.fun_fact && (
                <p className="text-gray-500 text-sm italic">More details coming soon. Contact us for more information about this course.</p>
              )}
            </div>
          )}
          
          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
            <div>
              {hasDiscount ? (
                <div>
                  <span className="text-sm text-gray-400 line-through">
                    {formatItemPrice(course.price, course.currency, { compact: true })}
                  </span>
                  <span className="text-2xl font-mono-accent font-bold text-[#FF6B35] ml-2">
                    {formatItemPrice(discountedPrice, course.currency, { compact: true })}
                  </span>
                  <Badge className="ml-2 bg-[#FF6B35] text-white text-xs">
                    {discountPercent}% OFF
                  </Badge>
                </div>
              ) : (
                <div>
                  <span className="text-2xl font-mono-accent font-bold text-[#0A2342]">
                    {formatItemPrice(course.price, course.currency, { compact: true })}
                  </span>
                  <div className="text-xs mt-1 space-y-0.5">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px] border-[#0A2342] text-[#0A2342]">Club</Badge>
                      <span className="text-gray-500">{formatItemPrice(course.price * 0.8, course.currency, { compact: true })}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px] border-[#FF6B35] text-[#FF6B35]">Pro</Badge>
                      <span className="text-gray-500">{formatItemPrice(course.price * 0.75, course.currency, { compact: true })}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <Button
              onClick={() => handleAddToCart(course)}
              className="bg-[#FF6B35] text-white hover:bg-[#FF6B35]/90"
              data-testid={`add-course-${course.id}`}
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              Add to Cart
            </Button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <Loader2 className="w-8 h-8 animate-spin text-[#0A2342]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-[#0A2342] mb-4">
            {header.heading}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl">
            {header.subheading}
          </p>
        </div>

        {/* Contact Notice */}
        <div className="bg-[#0A2342] text-white p-6 rounded-lg mb-12">
          <div className="flex items-start gap-3">
            <GraduationCap className="w-6 h-6 text-[#FF6B35] flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold text-lg mb-1">More Courses Available</h3>
              <p className="text-gray-300 text-sm">
                Contact us for additional specialty courses and custom training packages.
              </p>
            </div>
          </div>
        </div>

        {/* Beginner Courses */}
        {beginnerCourses.length > 0 && (
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-8 bg-[#FF6B35] rounded-full"></div>
              <h2 className="text-2xl font-heading font-semibold text-[#0A2342]">Start Here</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {beginnerCourses.map((course) => (
                <CourseCard key={course.id} course={course} featured={course.name.toLowerCase().includes('open water')} />
              ))}
            </div>
          </section>
        )}

        {/* Advanced Courses */}
        {advancedCourses.length > 0 && (
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-8 bg-[#0A2342] rounded-full"></div>
              <h2 className="text-2xl font-heading font-semibold text-[#0A2342]">Continue Your Training</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {advancedCourses.map((course) => (
                <CourseCard key={course.id} course={course} featured={course.name.toLowerCase().includes('divemaster')} />
              ))}
            </div>
          </section>
        )}

        {/* Professional Courses (Divemaster, Instructor) */}
        {professionalCourses.length > 0 && (
          <section className="mb-12" data-testid="professional-courses-section">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-8 bg-purple-600 rounded-full"></div>
              <h2 className="text-2xl font-heading font-semibold text-[#0A2342]">Go Professional</h2>
              <span className="ml-2 px-2 py-0.5 text-xs font-semibold rounded-full bg-purple-100 text-purple-700 uppercase tracking-wider">Pro Path</span>
            </div>
            <p className="text-gray-600 text-sm mb-6 max-w-2xl">
              Turn your passion into a career. Dive Master and Instructor programmes — the next step beyond Rescue Diver.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {professionalCourses.map((course) => (
                <CourseCard key={course.id} course={course} featured />
              ))}
            </div>
          </section>
        )}

        {/* Specialty Courses */}
        {specialtyCourses.length > 0 && (
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-8 bg-gray-400 rounded-full"></div>
              <h2 className="text-2xl font-heading font-semibold text-[#0A2342]">Specialty Programmes</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {specialtyCourses.map((course) => (
                <CourseCard key={course.id} course={course} />
              ))}
            </div>
            <div className="mt-6 bg-[#F0EFEA] p-4 rounded-lg">
              <p className="text-gray-700">
                <span className="font-semibold">More specialties available.</span> Contact us for the full list.
              </p>
            </div>
          </section>
        )}

        {/* Empty State */}
        {courses.length === 0 && !loading && (
          <div className="text-center py-12 bg-white rounded-lg border">
            <GraduationCap className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No Courses Available</h3>
            <p className="text-gray-500">Please check back later for our diving courses.</p>
          </div>
        )}

        {/* Important Info */}
        <section className="bg-white rounded-lg p-8 border border-gray-100">
          <h3 className="text-xl font-heading font-semibold text-[#0A2342] mb-4">Important Information</h3>
          <ul className="space-y-3 text-gray-600">
            <li className="flex items-start gap-2">
              <span className="text-[#FF6B35] font-bold">•</span>
              <span>All training is conducted in accordance with internationally recognised ISO-based scuba diving training standards.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#FF6B35] font-bold">•</span>
              <span>Certification is awarded only upon successful completion of all required theory, confined water, and open water components.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#FF6B35] font-bold">•</span>
              <span>Students must meet medical fitness and performance requirements. Payment alone does not guarantee certification.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#FF6B35] font-bold">•</span>
              <span>Training may be paused or extended for safety reasons. Additional sessions can be arranged if needed.</span>
            </li>
          </ul>
          
          <div className="mt-6 pt-6 border-t border-gray-100">
            <p className="text-sm text-gray-500">
              Questions about courses? Check our{' '}
              <Link to="/faq" className="text-[#FF6B35] hover:underline">FAQ section</Link> or{' '}
              <Link to="/terms" className="text-[#FF6B35] hover:underline">Terms & Conditions</Link>.
            </p>
          </div>
        </section>

        {/* Contact Banner */}
        <div className="mt-12">
          <ContactBanner whatsappNumber="+27 67 535 5405" />
        </div>
      </div>
    </div>
  );
};

export default CoursesPage;
